import { useOutletContext } from 'react-router-dom';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import ExportRunsButton from '../../../components/AiSpend/ExportRunsButton';
import PricingStatusBadge from '../../../components/AiSpend/PricingStatusBadge';
import ProviderBadge from '../../../components/AiSpend/ProviderBadge';
import RunDetailModal from '../../../components/AiSpend/RunDetailModal';
import UsageCell from '../../../components/AiSpend/UsageCell';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import Pagination from '../../../components/Table/Pagination';
import { getAiSpendRuns } from '../../../services/aiSpendService';
import {
  formatCount,
  formatInstant,
  formatLatency,
  formatMoney,
} from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';

export default function AiSpendRuns() {
  const { filters, setFilters, queryParams, queryKey, options } = useOutletContext();
  const ordering = filters.ordering || '-created';

  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) =>
      getAiSpendRuns({
        params: {
          ...queryParams,
          ordering,
          page: filters.page,
          page_size: filters.page_size,
        },
        signal,
      }),
    { deps: [queryKey] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            Individual calls
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            One row per physical call — a retry, a transport fallback and a correction
            pass each appear separately, because each of them cost money.
          </p>
        </div>
        <ExportRunsButton params={queryParams} rowCount={data?.count || 0} />
      </div>

      <AnalyticsTable
        loading={loading}
        rows={data?.results || []}
        rowKey={(row) => row.uuid}
        sort={ordering}
        onSortChange={(next) => setFilters({ ordering: next })}
        // The open run lives in the URL, so a specific call is linkable and the
        // browser Back button closes the dialog.
        onRowClick={(row) => setFilters({ run: row.uuid }, { keepPage: true })}
        columns={[
          {
            key: 'created',
            label: 'When',
            sortKey: 'created',
            render: (row) => formatInstant(row.created, options?.timezone),
          },
          {
            key: 'provider',
            label: 'Provider',
            render: (row) => <ProviderBadge provider={row.provider} />,
          },
          { key: 'model', label: 'Model', render: (row) => row.model },
          { key: 'feature_label', label: 'Feature', render: (row) => row.feature_label },
          {
            key: 'subject_email',
            label: 'Student',
            render: (row) => row.subject_email || '—',
          },
          {
            key: 'usage',
            label: 'Usage',
            render: (row) => <UsageCell usage={row.usage} billingUnit={row.billing_unit} />,
          },
          {
            key: 'cost_usd',
            label: 'Cost',
            sortKey: 'cost_micro_usd',
            render: (row) => (
              <span className="whitespace-nowrap font-semibold">
                {formatMoney(row.cost_usd)}
                <PricingStatusBadge status={row.pricing_status} />
              </span>
            ),
          },
          {
            key: 'latency_ms',
            label: 'Latency',
            sortKey: 'latency_ms',
            render: (row) => formatLatency(row.latency_ms),
          },
          {
            key: 'status',
            label: 'Outcome',
            render: (row) => {
              if (!row.success) {
                return (
                  <span className="text-red-600 dark:text-red-400">
                    {row.error_class || 'Failed'}
                  </span>
                );
              }
              if (!row.usable) {
                return (
                  <span className="text-amber-600 dark:text-amber-400">
                    Billed, unusable
                  </span>
                );
              }
              return <span className="text-emerald-600 dark:text-emerald-400">OK</span>;
            },
          },
        ]}
        footer={
          <Pagination
            page={filters.page}
            pageSize={filters.page_size}
            count={data?.count || 0}
            itemLabel="calls"
            onPageChange={(page) => setFilters({ page }, { keepPage: true })}
          />
        }
      />

      <p className="text-xs text-slate-500 dark:text-slate-400">
        {formatCount(data?.count || 0)} calls match these filters.
      </p>

      <RunDetailModal
        runUuid={filters.run}
        timezone={options?.timezone}
        onClose={() => setFilters({ run: '' }, { keepPage: true })}
      />
    </div>
  );
}
